<template>
  <div class="promotions-page">
    <v-alert v-if="!arenaId" type="info" dense class="mb-4">
      {{ $t('entities.promotions.selectArenaReminder') }}
    </v-alert>
    <v-row v-if="isLoadingPromotions" dense align="stretch" justify="start">
      <v-col
        v-for="i in currentItemsPerPage"
        :key="`skeleton-${i}`"
        cols="12"
        md="6"
        class="d-flex">
        <PromotionSkeletonLoader />
      </v-col>
    </v-row>
    <template v-else>
      <v-row class="flex-md-row-reverse mb-2">
        <v-col cols="12" md="3" class="d-flex justify-end align-end">
          <v-btn
            color="primary"
            depressed
            class="text-none"
            :disabled="!arenaId"
            @click="handleAddPromotion">
            <v-icon left small>mdi-plus</v-icon>
            {{ $t('entities.promotions.addPromotion') }}
          </v-btn>
        </v-col>
        <v-spacer />
        <v-col cols="12" md="3">
          <div v-if="arenaId" class="d-flex flex-column">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('miscellaneous.active') }}
            </div>
            <v-select
              :value="activeFilter"
              :items="activeFilterOptions"
              :disabled="isPaginationControlDisabled"
              item-text="text"
              item-value="value"
              solo
              dense
              hide-details
              @change="handleActiveFilterChange" />
          </div>
        </v-col>
        <v-col cols="12" md="3">
          <div v-if="arenaId" class="d-flex flex-column">
            <div class="text-caption font-weight-medium mb-1">
              {{ $t('components.rowsPerPage.entries') }}
            </div>
            <v-select
              :value="currentItemsPerPage"
              :items="itemsPerPageOptions"
              :disabled="isPaginationControlDisabled"
              solo
              dense
              hide-details
              @change="handlePromotionsItemsPerPageChange" />
          </div>
        </v-col>
      </v-row>

      <v-alert
        v-if="arenaId && !promotions.length"
        type="info"
        outlined
        class="mb-0">
        {{ $t('entities.promotions.noPromotions') }}
      </v-alert>

      <v-row v-else dense align="stretch" justify="start">
        <v-col
          v-for="promotion in promotions"
          :key="getPromotionKey(promotion)"
          cols="12"
          md="6"
          class="d-flex">
          <PromotionUpsertCard
            :promotion="promotion"
            :categories="categories"
            :arena-currency="arenaCurrency"
            :is-editing="isCardEditing(promotion)"
            :is-new="!promotion.id"
            :is-saving="isCardSaving(promotion)"
            :server-error="getServerError(promotion)"
            @request-edit="handleRequestEdit"
            @save="handleSave"
            @cancel="handleCancel"
            @delete="handleDelete" />
        </v-col>
      </v-row>
      <div
        v-if="shouldDisplayPagination"
        class="d-flex flex-column align-center mt-6">
        <v-pagination
          :value="pagination.current_page"
          :length="pagination.last_page || totalPages"
          :disabled="isPaginationControlDisabled"
          :total-visible="5"
          color="primary"
          @input="handlePromotionsPageChange" />
        <div
          v-if="paginationSummary"
          class="text-caption font-weight-light mt-2 text-center">
          {{ paginationSummary }}
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Promotion from '@/models/shop/Promotion'
import Category from '@/models/shop/CategoryV2'
import Currency from '@/models/shop/Currency'
import thousandSeparator from '@/mixins/thousand-separator'
import PromotionUpsertCard from './upsert-card/index.vue'
import PromotionSkeletonLoader from './PromotionSkeletonLoader.vue'

const DEFAULT_PAGE_SIZE = 6
const DEFAULT_ITEMS_PER_PAGE_OPTIONS = [6, 10, 14, 18]
const resetPagination = () => ({
  current_page: 1,
  per_page: DEFAULT_PAGE_SIZE,
  total: 0,
  last_page: 1,
})

export default {
  name: 'Promotions',
  mixins: [thousandSeparator],
  components: {
    PromotionUpsertCard,
    PromotionSkeletonLoader,
  },
  data() {
    return {
      promotions: [],
      isLoadingPromotions: false,
      editingCards: {}, // editingCards keeps the editing state keyed by promotion id/tempId.
      savingCards: {},
      saveErrors: {},
      categories: [],
      pagination: resetPagination(),
      activeFilter: null,
      arenaCurrency: null,
      isLoadingCurrency: false,
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena?.id
    },
    currentItemsPerPage() {
      const size = Number(this.pagination.per_page)
      return !Number.isNaN(size) && size > 0 ? size : DEFAULT_PAGE_SIZE
    },
    totalPages() {
      const perPage = this.currentItemsPerPage
      const total = Number(this.pagination.total) || this.promotions.length || 1
      return Math.max(1, Math.ceil(total / perPage))
    },
    paginationSummary() {
      const total = Number(this.pagination.total) || 0
      if (!total) return ''
      const perPage = this.currentItemsPerPage
      const currentPage = Number(this.pagination.current_page) || 1
      const visibleCount = this.promotions.length || 0
      const rawFrom = (currentPage - 1) * perPage + 1
      const rawTo = rawFrom + Math.max(visibleCount, 0) - 1
      const boundedFrom = Math.min(rawFrom, total)
      const boundedTo = Math.min(Math.max(boundedFrom, rawTo), total)
      if (!boundedFrom || boundedTo < boundedFrom) return ''
      const formattedFrom = this.addThousandSeparator(boundedFrom)
      const formattedTo = this.addThousandSeparator(boundedTo)
      const formattedTotal = this.addThousandSeparator(total)
      return `${formattedFrom} - ${formattedTo} ${this.$t(
        'components.pagination.of',
      )} ${formattedTotal}`
    },
    shouldDisplayPagination() {
      return (
        !!this.arenaId &&
        ((this.promotions && this.promotions.length > 0) ||
          (this.pagination.total && this.pagination.total > 0))
      )
    },
    itemsPerPageOptions() {
      return DEFAULT_ITEMS_PER_PAGE_OPTIONS.includes(this.currentItemsPerPage)
        ? DEFAULT_ITEMS_PER_PAGE_OPTIONS
        : [this.currentItemsPerPage, ...DEFAULT_ITEMS_PER_PAGE_OPTIONS]
    },
    activeFilterOptions() {
      return [
        { value: null, text: this.$t('components.rowsPerPage.all') },
        { value: true, text: this.$t('miscellaneous.active') },
        { value: false, text: this.$t('miscellaneous.inactive') },
      ]
    },
    isPaginationControlDisabled() {
      const hasRemotePromotions =
        typeof this.pagination.total === 'number' && this.pagination.total > 0
      return (
        this.isLoadingPromotions ||
        (!this.promotions.length && !hasRemotePromotions)
      )
    },
  },
  watch: {
    arenaId: {
      immediate: true,
      handler() {
        if (!this.arenaId) {
          this.promotions = []
          this.editingCards = {}
          this.categories = []
          this.pagination = resetPagination()
          this.activeFilter = null
          this.arenaCurrency = null
          return
        }
        this.pagination = resetPagination()
        this.activeFilter = null
        this.fetchPromotions()
        this.fetchCategories()
        this.fetchCurrencies()
      },
    },
  },
  methods: {
    async fetchCurrencies() {
      if (!this.arenaId) return
      this.isLoadingCurrency = true
      try {
        const response = await Currency.api.getArenaCurrencies(this.arenaId)
        const dataArray = response.data?.data ?? []
        const currencies = dataArray.map((it) => new Currency(it))
        this.arenaCurrency = currencies[0] || null
      } catch (error) {
        console.error(error)
        this.arenaCurrency = null
      } finally {
        this.isLoadingCurrency = false
      }
    },
    async fetchPromotions(overrides = {}) {
      if (!this.arenaId) return
      this.isLoadingPromotions = true
      try {
        const requestParams = this.getPromotionsParams(overrides)
        const response = await Promotion.api.list(this.arenaId, requestParams)
        const promotionDatas = response.data?.data || []
        this.promotions = promotionDatas.map((it) => new Promotion(it))
        this.editingCards = {}
        const metadata = response.data?.metadata
        this.updatePromotionsPagination(metadata, promotionDatas.length)
      } catch (error) {
        console.error(error)
        this.promotions = []
        this.editingCards = {}
        this.pagination = resetPagination()
      } finally {
        this.isLoadingPromotions = false
      }
    },
    async fetchCategories() {
      try {
        const response = await Category.api.get()
        const responseData = response.data?.data || []
        this.categories = responseData.map((data) => new Category(data))
      } catch (error) {
        console.error(error)
        this.categories = []
      }
    },
    handleAddPromotion() {
      if (!this.arenaId) return
      const blankPromotion = this.createBlankPromotion()
      this.promotions = [blankPromotion, ...this.promotions]
      this.setEditingState(blankPromotion, true)
      this.clearServerErrorByPromotion(blankPromotion)
    },
    getPromotionKey(promotion) {
      return promotion.id || promotion.tempId || promotion.name
    },
    isCardEditing(promotion) {
      return Boolean(this.editingCards[this.getPromotionKey(promotion)])
    },
    isCardSaving(promotion) {
      return Boolean(this.savingCards[this.getPromotionKey(promotion)])
    },
    getServerError(promotion) {
      return this.saveErrors[this.getPromotionKey(promotion)] || ''
    },
    handleRequestEdit(promotion) {
      this.setEditingState(promotion, true)
    },
    setEditingState(promotion, value) {
      const key = this.getPromotionKey(promotion)
      if (!key) return
      this.$set(this.editingCards, key, value)
    },
    setSavingStateByKey(key, value) {
      if (!key) return
      this.$set(this.savingCards, key, value)
    },
    setServerErrorByKey(key, message) {
      if (!key) return
      if (!message) {
        this.$delete(this.saveErrors, key)
        return
      }
      this.$set(this.saveErrors, key, message)
    },
    clearServerErrorByKey(key) {
      if (!key) return
      this.$delete(this.saveErrors, key)
    },
    clearServerErrorByPromotion(promotion) {
      this.clearServerErrorByKey(this.getPromotionKey(promotion))
    },
    async handleSave(updatedPromotion) {
      const key =
        updatedPromotion.id || updatedPromotion.tempId || updatedPromotion.name
      if (!key) return
      this.setSavingStateByKey(key, true)
      this.setServerErrorByKey(key, '')

      try {
        const persistedPromotion = updatedPromotion.id
          ? await this.updatePromotion(updatedPromotion)
          : await this.createPromotion(updatedPromotion)
        const syncedPromotion = await this.syncPromotionGroups(
          persistedPromotion,
          updatedPromotion.groups,
        )
        this.promotions = this.promotions.map((promotion) =>
          this.getPromotionKey(promotion) === key ? syncedPromotion : promotion,
        )
        if (!updatedPromotion.id) {
          this.incrementPromotionsTotal()
        }
        this.clearServerErrorByKey(key)
        this.$delete(this.editingCards, key)
      } catch (error) {
        this.setServerErrorByKey(key, this.extractErrorMessage(error))
      } finally {
        this.setSavingStateByKey(key, false)
      }
    },
    handleCancel(promotion) {
      const key = this.getPromotionKey(promotion)
      if (!key) return
      if (!promotion.id) {
        this.promotions = this.promotions.filter(
          (existing) => this.getPromotionKey(existing) !== key,
        )
        this.$delete(this.editingCards, key)
        this.$delete(this.savingCards, key)
        this.$delete(this.saveErrors, key)
        return
      }
      this.$delete(this.editingCards, key)
      this.$delete(this.savingCards, key)
      this.$delete(this.saveErrors, key)
    },
    handleDelete(promotion) {
      const key = this.getPromotionKey(promotion || {})
      if (!key) return
      this.promotions = this.promotions.filter(
        (existing) => this.getPromotionKey(existing) !== key,
      )
      this.$delete(this.editingCards, key)
      this.$delete(this.savingCards, key)
      this.$delete(this.saveErrors, key)
      if (promotion?.id) {
        this.decrementPromotionsTotal()
      }
    },
    createBlankPromotion() {
      return {
        id: null,
        tempId: `temp-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        arena_id: this.arenaId,
        name: '',
        image: '',
        description: '',
        discount_value: '',
        discount_type: 'fixed_price',
        standard_price: '',
        promo_price: '',
        sort_order: 1,
        active: false,
        groups: [],
      }
    },
    async updatePromotion(payload) {
      if (!this.arenaId || !payload.id) {
        return new Promotion({ ...payload })
      }
      const sanitized = this.sanitizePromotionPayload(payload)
      const response = await Promotion.api.update(
        this.arenaId,
        payload.id,
        sanitized,
      )
      const data = response.data || payload
      return new Promotion(data)
    },
    async createPromotion(payload) {
      if (!this.arenaId) {
        return new Promotion({
          ...payload,
          id: payload.id || `promotion-${Date.now()}`,
          tempId: null,
        })
      }
      const sanitized = this.sanitizePromotionPayload(payload)
      const response = await Promotion.api.create(this.arenaId, sanitized)
      const data = response.data || payload
      return new Promotion({ ...data })
    },
    sanitizePromotionPayload(payload) {
      const { standard_price, promo_price, image, description, ...rest } =
        payload
      const sanitizedRest = { ...rest }
      delete sanitizedRest.tempId
      const parseNumeric = (value) => {
        if (value === '' || value === null || value === undefined) return 0
        const number = Number(value)
        return Number.isNaN(number) ? 0 : number
      }
      const sanitizedPayload = {
        ...sanitizedRest,
        discount_value:
          typeof sanitizedRest.discount_value === 'string'
            ? sanitizedRest.discount_value.trim()
            : sanitizedRest.discount_value,
        standard_price: parseNumeric(standard_price),
        promo_price: parseNumeric(promo_price),
      }
      if (image) sanitizedPayload.image = image
      if (description) sanitizedPayload.description = description
      return sanitizedPayload
    },
    async syncPromotionGroups(promotion, groups) {
      const sanitizedGroups = (groups || [])
        .map((group) => this.sanitizePromotionGroup(group))
        .filter(Boolean)
      if (!sanitizedGroups.length) {
        return new Promotion({ ...promotion, groups: [] })
      }
      if (!this.arenaId || !promotion?.id) {
        const fallbackGroups = sanitizedGroups.map(
          ({ id, tempId, payload, existingVariants }) => ({
            id:
              id ||
              tempId ||
              `temp-group-${Date.now()}-${Math.random().toString(16).slice(2)}`,
            ...payload,
            variants: existingVariants,
          }),
        )
        return new Promotion({
          ...promotion,
          groups: fallbackGroups,
        })
      }
      const persistedGroups = []
      for (const group of sanitizedGroups) {
        const { id, payload, variantIds, existingVariants } = group
        const updatePayload = { ...payload }
        if (
          Object.prototype.hasOwnProperty.call(updatePayload, 'category_id')
        ) {
          delete updatePayload.category_id
        }
        const response = id
          ? await Promotion.api.updatePromotionGroup(
              this.arenaId,
              promotion.id,
              id,
              updatePayload,
            )
          : await Promotion.api.createPromotionGroup(
              this.arenaId,
              promotion.id,
              payload,
            )
        const responseData = response.data || {}
        const savedGroupId = responseData.id || id
        const baseVariants = Array.isArray(responseData.variants)
          ? responseData.variants
          : existingVariants
        const syncedVariants = await this.syncPromotionGroupVariants(
          promotion.id,
          savedGroupId,
          variantIds,
          baseVariants,
        )
        persistedGroups.push({
          ...payload,
          ...responseData,
          id: savedGroupId,
          variants: syncedVariants,
          variantIds: [],
        })
      }
      return new Promotion({
        ...promotion,
        groups: persistedGroups,
      })
    },
    sanitizePromotionGroup(group) {
      if (!group) return null
      const quantityValue = Number(group.quantity)
      const normalizedQuantity = Number.isNaN(quantityValue) ? 0 : quantityValue
      const allowedVariantIds = Array.isArray(group.variantIds)
        ? group.variantIds
            .map((variantId) => {
              if (typeof variantId === 'string') return variantId.trim()
              if (variantId === null || variantId === undefined) return ''
              return String(variantId)
            })
            .filter((variantId) => Boolean(variantId))
        : []
      const uniqueVariantIds = []
      const seenVariantIds = new Set()
      allowedVariantIds.forEach((variantId) => {
        if (seenVariantIds.has(variantId)) return
        seenVariantIds.add(variantId)
        uniqueVariantIds.push(variantId)
      })
      const normalizedId = this.isPersistedGroup(group) ? group.id : ''
      return {
        id: normalizedId,
        tempId: group.tempId || '',
        payload: {
          name: group.name || '',
          quantity: normalizedQuantity,
          category_id: group.category_id,
        },
        variantIds: uniqueVariantIds,
        existingVariants: Array.isArray(group.variants)
          ? group.variants.map((variant) => ({ ...variant }))
          : [],
      }
    },
    isPersistedGroup(group) {
      if (!group || !group.id) return false
      if (group.tempId && group.tempId === group.id) return false
      return true
    },
    async syncPromotionGroupVariants(
      promotionId,
      groupId,
      variantIds,
      existingVariants = [],
    ) {
      if (
        !this.arenaId ||
        !promotionId ||
        !groupId ||
        !Array.isArray(variantIds) ||
        !variantIds.length
      ) {
        return existingVariants
      }
      const createdVariants = []
      for (const variantId of variantIds) {
        try {
          const response = await Promotion.api.createPromotionGroupVariant(
            this.arenaId,
            promotionId,
            groupId,
            { arena_variant_id: variantId },
          )
          if (response.data) {
            createdVariants.push(response.data)
          }
        } catch (error) {
          const apiMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            ''
          if (
            typeof apiMessage === 'string' &&
            apiMessage.toLowerCase().includes('already exists')
          ) {
            console.warn(
              `Skipping duplicate variant ${variantId} for promotion group ${groupId}`,
            )
            continue
          }
          throw error
        }
      }
      return [...existingVariants, ...createdVariants]
    },
    extractErrorMessage(error) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message
      return apiMessage || this.$t('errors.default')
    },
    getPromotionsParams(overrides = {}) {
      const page =
        Number(overrides.page) || Number(this.pagination.current_page) || 1
      const size =
        Number(overrides.size) ||
        Number(this.pagination.per_page) ||
        DEFAULT_PAGE_SIZE
      const activeFilter = overrides.active ?? this.activeFilter
      const normalizedPage = page > 0 ? page : 1
      const normalizedSize = size > 0 ? size : DEFAULT_PAGE_SIZE
      return {
        page: normalizedPage,
        size: normalizedSize,
        ...(activeFilter !== null ? { active: activeFilter } : {}),
      }
    },
    updatePromotionsPagination(metadata = null, fallbackLength = 0) {
      const normalizedPage =
        Number(metadata?.page) || this.pagination.current_page || 1
      const normalizedSize =
        Number(metadata?.size) || this.pagination.per_page || DEFAULT_PAGE_SIZE
      let totalFromMetadata = null
      if (metadata) {
        if (Object.prototype.hasOwnProperty.call(metadata, 'count')) {
          totalFromMetadata = Number(metadata.count)
        } else if (Object.prototype.hasOwnProperty.call(metadata, 'total')) {
          totalFromMetadata = Number(metadata.total)
        } else if (
          Object.prototype.hasOwnProperty.call(metadata, 'total_count')
        ) {
          totalFromMetadata = Number(metadata.total_count)
        }
      }
      const normalizedTotal =
        totalFromMetadata !== null && !Number.isNaN(totalFromMetadata)
          ? totalFromMetadata
          : fallbackLength
      const pageSize = normalizedSize > 0 ? normalizedSize : DEFAULT_PAGE_SIZE
      const lastPage =
        normalizedTotal > 0
          ? Math.max(1, Math.ceil(normalizedTotal / pageSize))
          : 1
      this.pagination = {
        current_page: normalizedPage || 1,
        per_page: pageSize,
        total: normalizedTotal || 0,
        last_page: lastPage || 1,
      }
    },
    handlePromotionsPageChange(page) {
      const normalizedPage = Number(page) || 1
      if (normalizedPage === this.pagination.current_page) return
      this.pagination = {
        ...this.pagination,
        current_page: normalizedPage,
      }
      this.fetchPromotions({
        page: normalizedPage,
        size: this.pagination.per_page,
      })
    },
    handlePromotionsItemsPerPageChange(size) {
      const normalizedSize = Number(size) || DEFAULT_PAGE_SIZE
      if (normalizedSize === this.pagination.per_page) return
      const normalizedPage = 1
      this.pagination = {
        ...this.pagination,
        current_page: normalizedPage,
        per_page: normalizedSize,
      }
      this.fetchPromotions({ page: normalizedPage, size: normalizedSize })
    },
    handleActiveFilterChange(value) {
      this.activeFilter = value === null ? null : Boolean(value)
      this.pagination = {
        ...this.pagination,
        current_page: 1,
      }
      this.fetchPromotions({ page: 1 })
    },
    incrementPromotionsTotal() {
      const nextTotal = (Number(this.pagination.total) || 0) + 1
      const perPage = this.currentItemsPerPage
      const safePerPage = perPage > 0 ? perPage : DEFAULT_PAGE_SIZE
      this.pagination = {
        ...this.pagination,
        total: nextTotal,
        last_page: Math.max(1, Math.ceil(nextTotal / safePerPage)),
      }
    },
    decrementPromotionsTotal() {
      const nextTotal = Math.max(0, (Number(this.pagination.total) || 0) - 1)
      const perPage = this.currentItemsPerPage
      const safePerPage = perPage > 0 ? perPage : DEFAULT_PAGE_SIZE
      const prevPage = this.pagination.current_page
      const nextLastPage = Math.max(1, Math.ceil(nextTotal / safePerPage))
      const nextPage = prevPage > nextLastPage ? nextLastPage : prevPage
      this.pagination = {
        ...this.pagination,
        total: nextTotal,
        last_page: nextLastPage,
        current_page: nextPage,
      }
      if (prevPage > nextLastPage) {
        this.fetchPromotions({
          page: nextPage,
          size: perPage,
        })
      }
    },
  },
}
</script>

<style scoped>
.promotions-page {
  position: relative;
  top: -58px;
}
</style>
